import React, { useContext, useState } from "react"
import { View, StyleSheet, ScrollView, Platform, Image } from "react-native"
import { connect } from "react-redux"
import { theme } from "@common/theme/theme"
import { openCalendly } from "../data/utility/Utils"
import TextM from "@common/components/text/TextM"
import { apiRequest, apiRequestWithPrerequest } from "@data/redux/actions/api"
import { fastTrackService } from "@data/services/fastTrackService"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { LinearGradient } from "expo-linear-gradient"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { SuccessBadge } from "@common/components/badge/SuccessBadge"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import logo from "../../assets/logo-blue.png"
import { RatingStars } from "@common/components/rating/RatingStars"
import base64 from "@common/utils/base64"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { useRequestOnMount } from "../common/hooks/useRequestOnMount"
import { CheckoutProvider } from "@stripe/react-stripe-js/checkout"
import { loadStripe } from "@stripe/stripe-js"
import { STRIPE_PUBLISHABLE_KEY } from "@data/constants/apiConstants"
import { useGenerateStagePreInscription } from "./hooks/useGenerateStagePreInscription"
import { StagePreInscriptionContent } from "./StagePreInscriptionContent"
import FastTrackRecognitionFlow from "./recognition/FastTrackRecognitionFlow"
import {
    RECOGNITION_OUTCOMES,
    trackRecognition,
} from "./recognition/recognitionFlow"

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

// Read a query param from window.location.search (web-only) — bypass React
// Navigation's linking layer which doesn't populate route.params from query
// strings for screens that haven't declared an explicit path template.
const readWebQueryParam = (...keys) => {
    if (typeof window === "undefined" || !window.location?.search) return null
    const params = new URLSearchParams(window.location.search)
    for (const k of keys) {
        const v = params.get(k)
        if (v) return v
    }
    return null
}

const FastTrackWelcomeScreen = (props) => {
    const { navigation, route } = props
    // The LP brevet redirects with ?submissionGUID=xxx (Lovable embed uses HubSpot's
    // {{submissionGuid}} token). Accept any casing — route.params first (for native
    // / programmatic navigation), then fall back to window.location.search (web).
    const submissionGuid =
        route.params?.submissionGuid ??
        route.params?.submissionGUID ??
        route.params?.submission_guid ??
        readWebQueryParam("submissionGUID", "submissionGuid", "submission_guid")
    const formGuidKey =
        route.params?.formGuidKey ??
        route.params?.form_guid_key ??
        readWebQueryParam("formGuidKey", "form_guid_key") ??
        "subscription_1_to_5"
    const dimensions = useContext(DimensionsContext)
    const containerWidth = dimensions?.width
    const insets = useSafeAreaInsets()
    const [user, setUser] = useState(null)
    const [checkoutSession, setCheckoutSession] = useState(null)
    // « On vous connaît déjà » : set when the form named a known family,
    // until the family lands on an account.
    const [recognition, setRecognition] = useState(null)

    // Detect if this is a stage/holiday_course form
    const isStageForm = formGuidKey === "holiday_course"
    // What the Amplitude event calls this request: the holiday-course form
    // carries no lead_intent, and neither does the trial.
    const trackedIntent = (leadIntent) =>
        leadIntent ?? (isStageForm ? "holiday_course" : "trial")

    // Where the page goes once it holds an account — straight from init, or
    // after « On vous connaît déjà ».
    const continueWith = (successData) => {
        setRecognition(null)
        if (successData?.lead_intent === "brevet_sprint_2026") {
            navigation.replace("brevet/start", {
                submissionGUID: submissionGuid,
            })
            return
        }
        // Cours d'été funnel — same shared `subscription_1_to_5` HubSpot
        // form, distinguished by a hidden `lead_intent` property. Route to
        // /summer/start, which re-uses the same submissionGUID to hydrate
        // the funnel from the HubSpot submission (SummerClassScreen → init).
        if (successData?.lead_intent === "summer_courses") {
            navigation.replace("summer/start", {
                submissionGUID: submissionGuid,
            })
            return
        }
        // Funnel d'inscription (cours réguliers, vendu toute l'année) —
        // même formulaire `subscription_1_to_5` partagé, distingué par la
        // valeur `inscription`. Égalité stricte et non préfixe : l'intent a
        // été arbitré PÉRENNE, sans millésime, pour que la landing n'ait
        // jamais à changer.
        //
        // Cette branche manquait : le funnel et son `/init` existaient,
        // mais aucune porte n'y menait — un lead qui soumettait le
        // formulaire avec le bon intent retombait dans le fast-track
        // standard, et la campagne n'aurait converti personne.
        if (successData?.lead_intent === "inscription") {
            navigation.replace("inscription/start", {
                submissionGUID: submissionGuid,
            })
            return
        }
        // Funnel stage générique (catalogue-driven) — toute valeur de
        // lead_intent préfixée `stage_` route vers /stage/start ; le
        // suffixe porte le stage/saison visé, résolu côté backend
        // (stage-funnel/init) en template du catalogue.
        if (successData?.lead_intent?.startsWith?.("stage_")) {
            navigation.replace("stage/start", {
                submissionGUID: submissionGuid,
                leadIntent: successData.lead_intent,
            })
            return
        }
        setUser(successData)
    }

    // Initialize fast track on mount.
    // The init response surfaces `lead_intent` inside `data` (the HubSpot LP
    // brevet form submits to the same `subscription_1_to_5` form but pre-fills
    // a hidden lead_intent property). When it equals "brevet_sprint_2026" we
    // replace the screen so the parent lands on /brevet/start instead of the
    // standard fast-track flow. `navigation.replace` (vs navigate) so the back
    // button can't reopen /register/fast-track in a loop.
    const { initialLoadingHack } = useRequestOnMount(
        props.apiRequestWithPrerequest,
        fastTrackService.initFastTrack,
        { body: { submission_guid: submissionGuid, form_guid_key: formGuidKey } },
        (successData) => {
            if (successData?.recognition) {
                setRecognition(successData)
                return
            }
            // A reload lands where the first visit did: counted once.
            if (!successData?.already_landed) {
                trackRecognition(
                    RECOGNITION_OUTCOMES.NOT_RECOGNIZED,
                    trackedIntent(successData?.lead_intent)
                )
            }
            continueWith(successData)
        },
        (error) => console.error("Fast track init error:", error)
    )

    const isLoading = initialLoadingHack || props.loading

    // Generate stage pre-inscription checkout session when stage form + user loaded
    useGenerateStagePreInscription({
        isLoading: isLoading || !isStageForm || !user?.email,
        apiRequest: props.apiRequest,
        email: user?.email,
        setCheckoutSession,
    })

    const handleCalendlyPress = () => {
        // Open Calendly with user data using existing helper
        openCalendly(user, "fast-track")

        // Navigate to thank you screen after Calendly opens
        // In production, this would be handled by Calendly callback
        setTimeout(() => {
            navigation.navigate("register/fast-track/thank-you", {
                formGuidKey: formGuidKey,
            })
        }, 5000)
    }

    const handleTakeAppointmentPress = () => {
        navigation.navigate("register/fast-track/diagnostic", {
            user: base64.encode(user?.email),
        })
    }

    const errorScreenProps = !submissionGuid
        ? {
              error: {
                  message:
                      "Veuillez reprendre depuis le formulaire pour accéder à cette page.",
              },
              onButtonPress: () => navigation.goBack(),
          }
        : props.error
        ? {
              error: props.error,
              onButtonPress: () =>
                  props.apiRequestWithPrerequest(
                      fastTrackService.initFastTrack,
                      {},
                      { submission_guid: submissionGuid, form_guid_key: formGuidKey }
                  ),
          }
        : props.checkoutError
        ? {
              error: props.checkoutError,
              onButtonPress: () =>
                  props.apiRequest(
                      fastTrackService.createStagePreInscription,
                      {},
                      { email: user?.email }
                  ),
          }
        : null

    if (recognition && !isLoading) {
        return (
            <ScreenSkeleton errorScreenProps={errorScreenProps}>
                <FastTrackRecognitionFlow
                    recognition={recognition.recognition}
                    trackedIntent={trackedIntent(recognition.lead_intent)}
                    navigation={navigation}
                    onLanded={continueWith}
                />
            </ScreenSkeleton>
        )
    }

    // Stage form with checkout session ready: show payment flow
    if (isStageForm && checkoutSession?.client_secret) {
        return (
            <ScreenSkeleton errorScreenProps={errorScreenProps}>
                <CheckoutProvider
                    key={checkoutSession.client_secret}
                    stripe={stripePromise}
                    options={{
                        fetchClientSecret: async () =>
                            checkoutSession.client_secret,
                    }}
                >
                    <StagePreInscriptionContent
                        user={user}
                        holidayCourse={checkoutSession.holiday_course}
                    />
                </CheckoutProvider>
            </ScreenSkeleton>
        )
    }

    // Stage form: show error screen if checkout failed
    if (isStageForm && !isLoading && user && props.checkoutError) {
        return (
            <ScreenSkeleton errorScreenProps={errorScreenProps} />
        )
    }

    // Stage form loading checkout session
    if (isStageForm && !isLoading && user) {
        return (
            <ScreenSkeleton
                errorScreenProps={errorScreenProps}
                isLoading
                loadingMessage="Préparation de votre réservation..."
            />
        )
    }

    // Regular flow (séance découverte) or initial loading
    return (
        <ScreenSkeleton
            errorScreenProps={errorScreenProps}
            isLoading={isLoading}
        >
            <View style={[styles.container, { paddingTop: insets.top }]}>
                <ScrollView
                    contentContainerStyle={[
                        styles.scrollContent,
                        { width: "100%" },
                    ]}
                    showsVerticalScrollIndicator={false}
                >
                    {/* Logo, Badge, Header, Rating, CTA Container */}
                    <View
                        style={[styles.topContainer, { width: containerWidth }]}
                    >
                        {/* Masteur Logo */}
                        <Image
                            source={logo}
                            style={styles.logo}
                            resizeMode="contain"
                        />

                        {/* Success Badge */}
                        <SuccessBadge text="Demande reçue avec succès" />

                        {/* Header */}
                        <View>
                            <TextM
                                fontSize={32}
                                fontWeight="ExtraBold"
                                color={theme.colors.grey}
                                style={styles.title}
                                wrap
                            >
                                Votre{" "}
                                <TextM
                                    fontSize={32}
                                    fontWeight="Black"
                                    color={theme.colors.primary}
                                    wrap
                                >
                                    séance découverte gratuite
                                </TextM>
                                {"\n"}vous attend !
                            </TextM>
                            <TextM
                                fontSize={16}
                                color={theme.colors.grey}
                                style={styles.subtitle}
                                wrap
                            >
                                Réservez votre{" "}
                                <TextM
                                    fontSize={16}
                                    fontWeight="Bold"
                                    color={theme.colors.primary}
                                    wrap
                                >
                                    séance découverte de 30 minutes pour{" "}
                                    {user?.firstname}
                                </TextM>{" "}
                                avec un tuteur expert. C&apos;est
                                gratuit et sans engagement !
                            </TextM>
                        </View>

                        {/* Rating and Stats */}
                        <View style={styles.ratingContainer}>
                            <RatingStars score={4.9} scale={5} />
                            <TextM fontSize={14} color={theme.colors.grey}>
                                20 000+ heures de cours
                            </TextM>
                        </View>

                        {/* Choice Options - Two clear CTAs */}
                        <View style={styles.choiceContainer}>
                            {/* Option 1: Reserve trial immediately */}
                            <TouchableRippleM
                                onPress={handleTakeAppointmentPress}
                                style={[styles.choiceButton]}
                            >
                                <LinearGradient
                                    colors={[
                                        theme.colors.success,
                                        theme.colors.successDark,
                                    ]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={styles.ctaGradient}
                                >
                                    <FontAwesome5
                                        name="gift"
                                        solid
                                        size={16}
                                        color={theme.colors.white}
                                    />
                                    <TextM
                                        fontSize={16}
                                        fontWeight="Bold"
                                        color={theme.colors.white}
                                    >
                                        Réserver la séance
                                    </TextM>
                                </LinearGradient>
                            </TouchableRippleM>

                            {/* Option 2: Be called back */}
                            <TouchableRippleM
                                onPress={handleCalendlyPress}
                                style={[styles.choiceButton]}
                            >
                                <View style={styles.secondaryCtaContent}>
                                    <FontAwesome5
                                        name="phone-alt"
                                        solid
                                        size={16}
                                        color={theme.colors.primary}
                                    />
                                    <TextM
                                        fontSize={16}
                                        fontWeight="Bold"
                                        color={theme.colors.primary}
                                    >
                                        Je souhaite être rappelé
                                    </TextM>
                                </View>
                            </TouchableRippleM>
                        </View>
                    </View>

                    {/* Testimonials Section */}
                    <View style={styles.testimonialsSection}>
                        <TextM
                            fontSize={18}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            style={styles.testimonialsTitle}
                        >
                            Ce que disent les parents
                        </TextM>

                        <View style={styles.testimonialsContainer}>
                            {Platform.OS === "web" ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: `
                                        <iframe height="800px" id='testimonialto-masteur-tag-all-light-animated' src="https://embed-v2.testimonial.to/w/masteur?animated=on&theme=light&shadowColor=e7eaf4&speed=1&tag=all&cc=off" frameborder="0" scrolling="no" width="100%"></iframe>
                                    `,
                                    }}
                                    style={styles.testimonialsIframe}
                                />
                            ) : (
                                <View style={styles.testimonialCard}>
                                    <TextM
                                        fontSize={14}
                                        color={theme.colors.grey}
                                        style={styles.testimonialText}
                                        wrap
                                    >
                                        &ldquo;Le suivi personnalisé est
                                        exceptionnel. Thomas a gagné 3 points de
                                        moyenne en physique !&rdquo;
                                    </TextM>
                                    <TextM
                                        fontSize={12}
                                        fontWeight="Bold"
                                        color={theme.colors.primary}
                                        style={styles.testimonialAuthor}
                                    >
                                        - Parent d&apos;élève
                                    </TextM>
                                </View>
                            )}
                        </View>
                    </View>

                    {/* CTA Card and Steps Container */}
                    <View
                        style={[
                            styles.bottomContainer,
                            { width: containerWidth },
                        ]}
                    >
                        {/* Main CTA Card */}
                        <View style={styles.ctaCard}>
                            <TextM
                                fontSize={20}
                                fontWeight="Bold"
                                color={theme.colors.primary}
                                style={styles.ctaTitle}
                            >
                                COURS D'ESSAI OFFERT
                            </TextM>

                            <TextM
                                fontSize={16}
                                color={theme.colors.grey}
                                style={styles.ctaDescription}
                                wrap
                            >
                                Cours 100% personnalisé • Bilan complet inclus • Zéro engagement
                            </TextM>

                            <View style={styles.choiceContainer}>
                                <TouchableRippleM
                                    onPress={handleTakeAppointmentPress}
                                    style={[styles.choiceButton]}
                                >
                                    <LinearGradient
                                        colors={[
                                            theme.colors.success,
                                            theme.colors.successDark,
                                        ]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={styles.ctaGradient}
                                    >
                                        <FontAwesome5
                                            name="gift"
                                            solid
                                            size={16}
                                            color={theme.colors.white}
                                        />
                                        <TextM
                                            fontSize={16}
                                            fontWeight="Bold"
                                            color={theme.colors.white}
                                        >
                                            Réserver la séance
                                        </TextM>
                                    </LinearGradient>
                                </TouchableRippleM>

                                <TouchableRippleM
                                    onPress={handleCalendlyPress}
                                    style={[styles.choiceButton]}
                                >
                                    <View style={styles.secondaryCtaContent}>
                                        <FontAwesome5
                                            name="phone-alt"
                                            solid
                                            size={16}
                                            color={theme.colors.primary}
                                        />
                                        <TextM
                                            fontSize={16}
                                            fontWeight="Bold"
                                            color={theme.colors.primary}
                                        >
                                            Je souhaite être rappelé
                                        </TextM>
                                    </View>
                                </TouchableRippleM>
                            </View>
                        </View>
                    </View>
                </ScrollView>
            </View>
        </ScreenSkeleton>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.whiteAlmost,
        alignItems: "center",
    },
    scrollContent: {
        flexGrow: 1,
        alignSelf: "center",
        alignItems: "center",
        paddingVertical: 40,
        paddingBottom: 100,
    },

    // Logo styles
    logo: {
        width: 120,
        height: 40,
    },

    // Container styles
    topContainer: {
        alignSelf: "center",
        alignItems: "center",
        paddingHorizontal: 20,
        gap: 30,
    },
    bottomContainer: {
        alignSelf: "center",
        alignItems: "center",
        paddingHorizontal: 20,
    },

    // Header styles
    title: {
        textAlign: "center",
        marginBottom: 16,
        lineHeight: 36,
    },
    subtitle: {
        textAlign: "center",
        lineHeight: 24,
    },

    // Rating styles
    ratingContainer: {
        alignItems: "center",
        gap: 8,
    },

    // Immediate CTA button
    immediateCtaButton: {
        marginBottom: 40,
        width: "100%",
        borderRadius: 15,
        overflow: "hidden",
    },

    // Choice options styles
    choiceContainer: {
        width: "100%",
        gap: 12,
        marginBottom: 20,
    },
    choiceButton: {
        width: "100%",
        borderRadius: 15,
        overflow: "hidden",
    },
    ctaGradient: {
        paddingVertical: 18,
        paddingHorizontal: 40,
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
        gap: 8,
    },
    secondaryCtaContent: {
        paddingVertical: 18,
        paddingHorizontal: 40,
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
        gap: 8,
        backgroundColor: theme.colors.white,
        borderWidth: 2,
        borderColor: theme.colors.primary,
        borderRadius: 15,
    },

    // Testimonials styles
    testimonialsSection: {
        marginTop: 50,
        paddingTop: 30,
        paddingHorizontal: 20,
        marginBottom: 30,
        alignSelf: "stretch",
        backgroundColor: theme.colors.material.primary[50],
    },
    testimonialsTitle: {
        textAlign: "center",
        marginBottom: 20,
    },
    testimonialsContainer: {
        backgroundColor: theme.colors.white,
        elevation: 2,
        overflow: "hidden",
    },
    testimonialsIframe: {
        width: "100%",
        minHeight: 400,
        backgroundColor: "transparent",
    },
    testimonialCard: {
        padding: 20,
        alignItems: "center",
    },
    testimonialText: {
        textAlign: "center",
        lineHeight: 20,
        marginBottom: 12,
        fontStyle: "italic",
    },
    testimonialAuthor: {
        textAlign: "center",
    },

    // CTA card styles
    ctaCard: {
        backgroundColor: theme.colors.white,
        padding: 24,
        borderRadius: 16,
        marginBottom: 30,
        width: "100%",
        alignItems: "center",
        elevation: 2,
        shadowColor: theme.colors.blackTransparent,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primaryLighter,
    },
    ctaTitle: {
        textAlign: "center",
        marginBottom: 16,
    },
    ctaDescription: {
        textAlign: "center",
        lineHeight: 24,
        marginBottom: 24,
    },
    ctaButton: {
        // Button styles now handled by LinearGradient inline styles
    },
})

// Redux connection following existing auth screen patterns
const mapStateToProps = (state) => {
    return {
        loading: state.api.initFastTrack?.request,
        success: state.api.initFastTrack?.success,
        error: state.api.initFastTrack?.error,
        isLoadingCheckout: state.api.createStagePreInscription?.request,
        checkoutError: state.api.createStagePreInscription?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiRequestWithPrerequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FastTrackWelcomeScreen)
